import { Form } from '@form/Form';
import { Blockquote, Button } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { FilterState } from 'components/DataTable/DataTable';
import { FormInput } from 'components/Form/next';
import Loader from 'components/Loader';
import { useApi } from 'hooks/useApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Category } from '../types';
import ServiceHoursPreviewTable from './ServiceHoursPreviewTable';
import ServiceHoursResultSummary from './ServiceHoursResultSummary';
import { ServiceHoursCommitResult, ServiceHoursPreviewResponse, ServiceHoursRowDecision } from './types';

interface Props {
    eventId: number;
    /** null means "every booking of the event" (table-wide selection). */
    bookingIds: number[] | null;
    /** Table filter in effect when bookingIds is null; forwarded so "select all" respects it server-side. */
    filter?: FilterState;
    callback?: () => void;
}

interface HeaderBridgeProps {
    onChange: (categoryId?: number, defaultHours?: number) => void;
}

// `Form`'s onChange is the plain DOM handler and cannot read react-hook-form values,
// so this child (rendered inside the FormProvider) watches the two header fields and
// pushes them up to the assistant.
const HeaderBridge = ({ onChange }: HeaderBridgeProps) => {
    const { control } = useFormContext();
    const [categoryId, defaultHours] = useWatch({ control, name: ['category_id', 'default_hours'] });

    useEffect(() => {
        onChange(
            categoryId === undefined || categoryId === '' ? undefined : Number(categoryId),
            defaultHours === undefined || defaultHours === '' ? undefined : Number(defaultHours)
        );
    }, [categoryId, defaultHours]);

    return null;
};

/** Assistant that credits service hours onto the members behind a set of event bookings. */
const BookingServiceHoursAssistant = ({ eventId, bookingIds, filter, callback }: Props) => {
    const { t } = useTranslation('bookings');
    const api = useApi();
    const clubSlug = useClubSlug();

    const [preview, setPreview] = useState<ServiceHoursPreviewResponse | null>(null);
    const [decisions, setDecisions] = useState<Record<number, ServiceHoursRowDecision>>({});
    const [categoryId, setCategoryId] = useState<number>();
    const [defaultHours, setDefaultHours] = useState<number>();
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<ServiceHoursCommitResult | null>(null);
    // Booking ids whose member assignment the user changed by hand.
    const touchedRows = useRef<Set<number>>(new Set());
    // Booking ids whose hours the user typed; those survive a change of the default.
    const touchedHours = useRef<Set<number>>(new Set());

    const { data: { data: categories } = {}, isLoading: isLoadingCategories } = useSWR<{ data: Category[] }>(
        clubSlug ? `club-member/${clubSlug}/category` : null
    );

    // Only categories that actually carry a service hours target can be credited against.
    const creditableCategories = useMemo(
        () => (categories ?? []).filter((category) => category.service_hours > 0),
        [categories]
    );

    // Must be memoized: the shared Form re-applies defaultValues on every render whose
    // object identity changed, so an inline literal here would snap the category select
    // back to the first entry each time anything else on the screen updates.
    const formDefaults = useMemo(
        () => ({ category_id: creditableCategories[0]?.id, default_hours: '' }),
        [creditableCategories]
    );

    // The preview is loaded once and never again: nothing on this screen changes what the
    // server would answer, which removes the whole class of stale-response bugs the sibling
    // assistant has to guard against.
    useEffect(() => {
        void api({
            url: `club-member/${clubSlug}/bookings/service-hours/preview`,
            method: 'POST',
            muted: true,
            data: { event_id: eventId, booking_ids: bookingIds, filter },
            onSuccess: (data: ServiceHoursPreviewResponse) => {
                setPreview(data);
                setDecisions(
                    Object.fromEntries(
                        data.rows.map((row) => [
                            row.booking_id,
                            {
                                action: row.suggested_action,
                                member_id: row.suggested_member_id,
                                hours: '' as const
                            }
                        ])
                    )
                );
            }
        });
    }, []);

    /** Re-seeds the hours of every row the user has not typed into. */
    useEffect(() => {
        setDecisions((current) =>
            Object.fromEntries(
                Object.entries(current).map(([bookingId, decision]) => [
                    bookingId,
                    touchedHours.current.has(Number(bookingId))
                        ? decision
                        : { ...decision, hours: defaultHours ?? ('' as const) }
                ])
            )
        );
    }, [defaultHours]);

    /** Stores the decision for one row and remembers what the user owns from now on. */
    const handleDecisionChange = useCallback((bookingId: number, decision: ServiceHoursRowDecision) => {
        touchedRows.current.add(bookingId);
        setDecisions((current) => {
            if (current[bookingId]?.hours !== decision.hours) touchedHours.current.add(bookingId);

            return { ...current, [bookingId]: decision };
        });
    }, []);

    /** Counts of each action plus the total, shown on the submit button. */
    const balance = useMemo(() => {
        const values = Object.values(decisions);
        const credited = values.filter((decision) => decision.action === 'credit' && decision.member_id);

        return {
            credit: credited.length,
            skip: values.length - credited.length,
            hours: credited.reduce((sum, decision) => sum + (Number(decision.hours) || 0), 0)
        };
    }, [decisions]);

    // Zero hours is not a no-op but a hard error: ServiceHoursService inlines the value
    // into the SQL expression and zero produces invalid SQL. Block the submit rather than
    // letting the backend answer 422 on something the form can see itself.
    const hasInvalidHours = useMemo(
        () =>
            Object.values(decisions).some(
                (decision) => decision.action === 'credit' && decision.member_id && !Number(decision.hours)
            ),
        [decisions]
    );

    /** Sends the decisions and shows the summary. */
    const handleSubmit = useCallback(
        (formData: FieldValues) => {
            setIsLoading(true);

            return api({
                url: `club-member/${clubSlug}/bookings/service-hours`,
                method: 'POST',
                data: {
                    event_id: eventId,
                    filter,
                    category_id: Number(formData.category_id),
                    rows: Object.entries(decisions).map(([bookingId, decision]) => ({
                        booking_id: Number(bookingId),
                        action: decision.action === 'credit' && decision.member_id ? 'credit' : 'skip',
                        member_id: decision.action === 'credit' ? decision.member_id : null,
                        hours: decision.action === 'credit' && decision.member_id ? Number(decision.hours) : null
                    }))
                },
                onBefore: () => setIsLoading(false),
                onSuccess: (commitResult: ServiceHoursCommitResult) => setResult(commitResult)
            });
        },
        [api, clubSlug, eventId, decisions, filter]
    );

    // Once the commit succeeded the summary replaces the form; only its close button ends the dialog.
    if (result) {
        return <ServiceHoursResultSummary result={result} onClose={() => callback?.()} />;
    }

    return (
        <Loader isLoading={!preview || isLoadingCategories}>
            <Blockquote>{t('booking.creditServiceHours.intro')}</Blockquote>

            <Form onSubmit={handleSubmit} defaultValues={formDefaults}>
                <HeaderBridge
                    onChange={(nextCategoryId, nextDefaultHours) => {
                        setCategoryId(nextCategoryId);
                        setDefaultHours(nextDefaultHours);
                    }}
                />

                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                    <FormInput.Select
                        name="category_id"
                        label={t('booking.creditServiceHours.categoryLabel')}
                        options={creditableCategories.map((category) => ({
                            key: category.id,
                            label: category.title || t('booking.creditServiceHours.unnamedCategory')
                        }))}
                    />
                    <FormInput
                        fullWidth
                        type="number"
                        step="0.5"
                        name="default_hours"
                        label={t('booking.creditServiceHours.defaultHoursLabel')}
                        placeholder="2"
                    />
                </div>

                {preview && (
                    <ServiceHoursPreviewTable
                        rows={preview.rows}
                        decisions={decisions}
                        memberCategories={preview.member_categories}
                        categoryId={categoryId}
                        onChange={handleDecisionChange}
                    />
                )}

                <Button
                    type="submit"
                    disabled={
                        isLoading ||
                        hasInvalidHours ||
                        balance.credit === 0 ||
                        !categoryId ||
                        preview?.rows.length === 0
                    }
                    className="tw-mt-4 tw-w-full"
                >
                    {t('booking.creditServiceHours.submit', balance)}
                </Button>
                {hasInvalidHours && (
                    <p className="tw-m-0 tw-mt-1 tw-text-center tw-text-sm tw-text-error">
                        {t('booking.creditServiceHours.invalidHours')}
                    </p>
                )}
            </Form>
        </Loader>
    );
};

export default BookingServiceHoursAssistant;
